import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { cn } from "../lib/utils.js";

const TooltipProvider = TooltipPrimitive.Provider;
const Tooltip = TooltipPrimitive.Root;
const TooltipTrigger = TooltipPrimitive.Trigger;

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <TooltipPrimitive.Content
    ref={ref}
    sideOffset={sideOffset}
    className={cn(
      // Inverted-neutral surface (foreground on background) instead of the
      // brand primary: tooltips are system chrome, not calls to action, so
      // the accent stays reserved for actual interactive emphasis.
      "z-50 overflow-hidden rounded-md bg-foreground px-3 py-1.5 text-xs text-background",
      // The one floating recipe (shared with select + dropdown-menu).
      // Radix Tooltip reports its open state as "delayed-open" or
      // "instant-open" (never plain "open"), so the enter classes gate on
      // both — an ungated animate-in would also replay on every re-render
      // remount and, on setups that pause CSS animations, strand the bubble
      // at its opacity-0 first frame. fill-mode-both pins the first/last
      // keyframe outside the active window; motion-reduce drops the
      // animation entirely.
      "data-[state=delayed-open]:animate-in data-[state=delayed-open]:fade-in-0 data-[state=delayed-open]:zoom-in-95",
      "data-[state=instant-open]:animate-in data-[state=instant-open]:fade-in-0 data-[state=instant-open]:zoom-in-95",
      "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
      "data-[side=bottom]:slide-in-from-top-0.5 data-[side=left]:slide-in-from-right-0.5 data-[side=right]:slide-in-from-left-0.5 data-[side=top]:slide-in-from-bottom-0.5",
      "duration-150 ease-out fill-mode-both motion-reduce:animate-none",
      className,
    )}
    {...props}
  />
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
